import PrivacyPolicyPage from 'Core/StaticPages/PrivacyPolicyPage';
import ProductDocumentationPage from 'Core/StaticPages/ProductDocumentationPage';
import TermsOfServicePage from 'Core/StaticPages/TermsOfServicePage';
import Dashboard from 'Features/Dashboard';
import { LoadingContent, NoContent } from 'Shared/PageElements';
import { PageWrapper } from 'Shared/PageWrapper';
import useSettingsFromState from 'State/Settings/Hooks/useSettingsFromState';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, Route, Switch } from 'react-router-dom';
import ConversationsPage from '../../Features/Conversations/Pages/ConversationsPage';
import NotificationsPage from '../../Features/Notifications/Pages/NotificationsPage';
import TasksPage from '../../Features/Tasks/Pages/TasksPage';
import { IsolatedLayout, StandardLayout } from '../Layouts';
import ownerOperatorApplications from '../RouteConfigurations/ownerOperatorApplications';
import ownerOperatorFeatures from '../RouteConfigurations/ownerOperatorFeatures';
import useFeatureApplications from '../RouteConfigurations/useFeatureApplications';
import useFeatureRoutes from '../RouteConfigurations/useFeatureRoutes';

const AccountFeature = React.lazy(() => import('Features/Account/AccountFeature'));

function OwnerOperatorRoot(): React.ReactElement {
    const { t } = useTranslation(['core']);
    const { hasError: hasSettingsError, isLoaded: isSettingsFullyLoaded, settings } = useSettingsFromState();
    const { featureApplications } = useFeatureApplications(ownerOperatorApplications);
    const { navigationLinks, featureRoutes } = useFeatureRoutes(ownerOperatorFeatures);

    return (
        <Switch>
            <Route path="/app">
                <Switch>
                    {featureApplications.map(({ url, PageRoot, LeftHeaderComponent, RightHeaderComponent }) => (
                        <Route path={url} key={`${url.toLowerCase().replace(' ', '-')}`}>
                            <IsolatedLayout
                                LeftHeaderComponent={LeftHeaderComponent}
                                RightHeaderComponent={RightHeaderComponent}
                            >
                                {PageRoot}
                            </IsolatedLayout>
                        </Route>
                    ))}
                    <Redirect strict from="/app" to="/" />
                </Switch>
            </Route>
            <Route path="/">
                <StandardLayout navigationLinks={navigationLinks}>
                    <Switch>
                        <Route exact path="/">
                            <Dashboard.Page />
                        </Route>
                        <Route strict path="/account">
                            <React.Suspense fallback={<LoadingContent />}>
                                <AccountFeature />
                            </React.Suspense>
                        </Route>
                        <Route strict path="/notifications">
                            <NotificationsPage />
                        </Route>
                        <Route strict path="/tasks">
                            <TasksPage />
                        </Route>
                        <Route strict path="/conversations">
                            <ConversationsPage />
                        </Route>
                        <Route strict path="/terms-of-service">
                            <TermsOfServicePage />
                        </Route>
                        <Route strict path="/privacy-policy">
                            <PrivacyPolicyPage />
                        </Route>
                        {!hasSettingsError && isSettingsFullyLoaded && settings?.helpGuides?.visible && (
                            <Route strict path="/product-documentation">
                                <ProductDocumentationPage />
                            </Route>
                        )}
                        {featureRoutes.map(
                            ({ url, text, ownerOperatorRoot }) =>
                                ownerOperatorRoot && (
                                    <Route path={url} key={`main-${text.toLowerCase().replace(' ', '-')}`}>
                                        {ownerOperatorRoot}
                                    </Route>
                                ),
                        )}
                        <Route path="*">
                            <PageWrapper pageTitle={t('core:notFound.title')}>
                                <NoContent message={t('core:notFound.subtitle')} />
                            </PageWrapper>
                        </Route>
                    </Switch>
                </StandardLayout>
            </Route>
        </Switch>
    );
}

export default OwnerOperatorRoot;
